import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import BootstrapTable from 'react-bootstrap-table-next';
import Pagination from 'components/v2/Pagination';
import { titleize, humanize } from 'underscore.string';
import { withTranslation } from 'react-i18next';
const colors = {
    available: 'bg-green',
    unavailable: 'bg-red-500',
    'In Progress': 'bg-teal-400',
    accepted: 'green',
    'Awaiting Booking': 'bg-purple-500',
};
const MAX_BUTTONS = 15;
const headerFormatter = (column, colIndex, { sortElement }) => {
    return (React.createElement("div", { className: "text-sm text-grey-darker" },
        React.createElement("strong", null, column.text),
        sortElement));
};
const statusFormatter = (cell, row) => {
    const bg = colors[cell] || 'bg-green';
    const classNames = classnames({
        'p-1': true,
        rounded: true,
        [bg]: true,
        'text-white': true,
        'text-sm': true,
        'font-bold': true,
    });
    return React.createElement("span", { className: classNames }, titleize(humanize(cell)));
};
const dateFormatter = (cell) => React.createElement("span", null, moment(cell).format('L'));
const booleanFormatter = (cell, row) => {
    let missingDocs = false;
    if (typeof row.missing_docs !== 'undefined') {
        missingDocs = row.missing_docs;
    }
    return React.createElement("span", null, missingDocs ? 'True' : 'False');
};
const priceFormatter = (cell) => React.createElement("span", null,
    "$",
    cell);
const entityFormatter = (cell) => (React.createElement("div", { className: "block overflow-hidden text-overflow-ellipsis" },
    React.createElement("a", { className: "" }, cell)));
const objectIdTimestampFormatter = (cell, row) => {
    const dateFromObjectId = function (objectId) {
        return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
    };
    return React.createElement("span", null, moment(dateFromObjectId(row._id)).format('YYYY-MM-DD'));
};
const customerFormatter = (type) => (cell, row) => {
    if (!cell)
        return null;
    let customer;
    let _id;
    switch (type) {
        case 'shipper':
            customer = cell;
            break;
        case 'carrier':
            customer = cell;
            if (!row.carrier_id) {
                return null;
            }
            _id =
                typeof row.carrier_id === 'string'
                    ? row.carrier_id
                    : row.carrier_id._id;
            break;
        default:
            customer = cell;
    }
    return (React.createElement("div", null,
        React.createElement("a", { className: "", href: `/users/${_id}`, target: "_blank", rel: "noreferrer" }, customer)));
};
const locationFormatter = (cell) => {
    if (!cell)
        return null;
    return (React.createElement("div", null,
        React.createElement("div", null,
            React.createElement("label", { className: "mr-2 text-sm font-bold text-grey-darker" }, cell.name)),
        React.createElement("span", { className: "mb-4" }, cell.address)));
};
const referenceNumberFormatter = (cell) => {
    const filtered = cell.filter((reference) => {
        return reference.name === 'Master Bill Of Lading';
    });
    const referenceNumbers = filtered.map((refNum) => (React.createElement("div", { className: "flex flex-col mb-2" },
        React.createElement("span", { className: "mb-4" }, refNum.value))));
    return React.createElement("div", null, referenceNumbers);
};
const expandFormatter = (toggleExpand, buttonText, isStaging, removeShipment, t) => (cell, row) => {
    return (React.createElement("div", { className: "flex flex-row" },
        React.createElement("button", { className: "button bg-blue-cm hover:bg-blue-cm-darker text-white mr-2", onClick: () => {
                toggleExpand();
            } }, buttonText || t('EDIT')),
        isStaging && (React.createElement("button", { className: "button bg-grey-dark hover:bg-grey-darker text-white", onClick: () => {
                removeShipment();
                // TODO: this will be a separate flux action eventually
            } }, "X"))));
};
class DataListAlt extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            expanded: false,
            showEditEquipmentModal: false,
        };
        this.getEntityFormatter = (type) => {
            switch (type) {
                case 'location':
                    return this.createResourceFormatter('locations');
                case 'order':
                    return this.createResourceFormatter('orders');
                case 'shipment':
                    return this.createResourceFormatter('shipments');
                case 'equipment':
                    return this.createResourceFormatter('equipment');
                default:
                    return entityFormatter;
            }
        };
        this.createResourceFormatter = (resource) => (cell, row) => {
            if (resource === 'equipment') {
                return (React.createElement("div", { className: "block", style: { display: 'flex', flexDirection: 'column' } }, cell.map((eq) => {
                    return (React.createElement("span", { style: { color: '#337ab7', cursor: 'pointer' }, onClick: () => this.props.onEquipmentClick(eq) }, eq.equipment_id));
                })));
            }
            const link = `/${resource}/${row._id}`;
            return (React.createElement("div", { className: "block overflow-hidden text-overflow-ellipsis" },
                React.createElement("a", { className: "", title: cell, href: link, target: "_blank", rel: "noreferrer" }, cell)));
        };
        this.toggleExpand = () => {
            this.setState({ expanded: !this.state.expanded });
        };
    }
    formatColumns() {
        let columns = this.props.columns.map((column) => {
            if (!this.props.showHeaders) {
                column.headerStyle = { display: 'none' };
            }
            else {
                // Somewhere deep, this impacts rendering multiple data list with
                // header styles
                if (column.headerStyle && column.headerStyle.display) {
                    delete column.headerStyle.display;
                }
            }
            if (this.props.noBorders) {
                column.style = { border: 0 };
            }
            if (!column.headerFormatter) {
                column.headerFormatter = headerFormatter;
            }
            if (!column.formatter) {
                switch (column.type) {
                    case 'price':
                        column.formatter = priceFormatter;
                        break;
                    case 'entity':
                        column.formatter = this.getEntityFormatter(column.entityType);
                        break;
                    case 'customer':
                        column.formatter = customerFormatter(column.entityType);
                        break;
                    case 'boolean':
                        column.formatter = booleanFormatter;
                        break;
                    case 'datetime':
                        column.formatter = dateFormatter;
                        break;
                    case 'reference_number':
                        column.formatter = referenceNumberFormatter;
                        break;
                    case 'location':
                        column.formatter = locationFormatter;
                        break;
                    case 'status':
                        column.formatter = statusFormatter;
                        break;
                    case 'objectId':
                        column.formatter = objectIdTimestampFormatter;
                        break;
                }
            }
            return column;
        });
        if (this.props.expandable) {
            columns.push({
                dataField: '',
                formatter: expandFormatter(this.toggleExpand, this.props.expandButtonText, this.props.isStaging, this.props.remove, this.props.t),
                headerStyle: { display: 'none' },
                style: {
                    borderTop: this.props.showHeaders ? '2px solid gainsboro' : '0px',
                },
            });
        }
        return columns;
    }
    componentWillReceiveProps(newProps) {
        if (newProps.editingCanceled && !this.props.editingCanceled) {
            this.setState({ expanded: false });
        }
    }
    render() {
        const columns = this.formatColumns();
        const selectRow = this.props.select
            ? {
                mode: 'checkbox',
                selected: this.props.selected,
                onSelect: this.props.handleOnSelect,
                onSelectAll: this.props.handleOnSelectAll,
                hideSelectColumn: this.props.hideSelectColumn,
                clickToSelect: this.props.clickToSelect,
            }
            : undefined;
        const rowStyle = null;
        const { t } = this.props;
        return (React.createElement("div", null,
            this.props.paginate && (React.createElement(Pagination, { first: true, last: true, total: this.props.total, page: this.props.page, onPageClick: this.props.onPaginationSelect, perPage: this.props.perPage })),
            React.createElement(BootstrapTable, { keyField: "_id", striped: this.props.striped, data: this.props.data, columns: columns, selectRow: selectRow, bordered: false, noDataIndication: this.props.noDataIndication || t('NO_DATA_FOUND'), rowStyle: rowStyle }),
            this.props.children
                ? this.props.children(this.state.expanded, this.toggleExpand)
                : null,
            this.props.paginate && (React.createElement(Pagination, { first: true, last: true, total: this.props.total, page: this.props.page, onPageClick: this.props.onPaginationSelect, perPage: this.props.perPage }))));
    }
}
DataListAlt.propTypes = {
    columns: PropTypes.array,
    select: PropTypes.bool,
};
export default withTranslation()(DataListAlt);
